import { useEffect, useState } from 'react';
import {
    fetchInterestCatalog,
    fetchMediaKit,
    InterestCategory,
    MediaKitData,
    saveMediaKitInterests,
} from '../services/mediaKitService';

interface MediaKitInterestsEditProps {
    selectedInterests: string[];
    customInterests: string;
    athleteUserId: number;
    onSaved: (data: MediaKitData) => void;
    onCancel: () => void;
}

function mapInterestNamesToIds(names: string[], categories: InterestCategory[]): number[] {
    const interestIdByName = new Map(
        categories.flatMap((category) =>
            category.interests.map((interest) => [interest.name.toLowerCase(), interest.id] as const)
        )
    );
    return names
        .map((name) => interestIdByName.get(name.toLowerCase()))
        .filter((id): id is number => id != null);
}

export default function MediaKitInterestsEdit({
    selectedInterests,
    customInterests,
    athleteUserId,
    onSaved,
    onCancel,
}: MediaKitInterestsEditProps) {
    const [interestCategories, setInterestCategories] = useState<InterestCategory[]>([]);
    const [catalogLoading, setCatalogLoading] = useState(true);
    const [catalogError, setCatalogError] = useState<string | null>(null);
    const [selectedInterestIds, setSelectedInterestIds] = useState<number[]>([]);
    const [customDraft, setCustomDraft] = useState(customInterests);
    const [activeCategoryIndex, setActiveCategoryIndex] = useState(0);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            try {
                setCatalogLoading(true);
                setCatalogError(null);
                const catalog = await fetchInterestCatalog();
                if (cancelled) {
                    return;
                }
                setInterestCategories(catalog);
                setSelectedInterestIds(mapInterestNamesToIds(selectedInterests, catalog));
                setActiveCategoryIndex(0);
            } catch {
                if (!cancelled) {
                    setCatalogError('Failed to load interest options.');
                }
            } finally {
                if (!cancelled) {
                    setCatalogLoading(false);
                }
            }
        };
        load();
        return () => {
            cancelled = true;
        };
    }, [selectedInterests]);

    const toggleInterest = (id: number) => {
        setSelectedInterestIds((prev) =>
            prev.includes(id) ? prev.filter((interestId) => interestId !== id) : [...prev, id]
        );
    };

    const save = async () => {
        setSaving(true);
        setSaveError(null);
        try {
            await saveMediaKitInterests(selectedInterestIds, customDraft, athleteUserId);
            const updated = await fetchMediaKit(athleteUserId);
            onSaved(updated);
        } catch (e: unknown) {
            setSaveError(e instanceof Error ? e.message : 'Failed to save interests');
        } finally {
            setSaving(false);
        }
    };

    const activeCategory = interestCategories[activeCategoryIndex];

    return (
        <div className="media-kit-interests-edit" data-testid="media-kit-interests-edit">
            {catalogLoading ? (
                <p className="media-kit-interests-loading">Loading interests...</p>
            ) : catalogError ? (
                <p className="media-kit-error">{catalogError}</p>
            ) : activeCategory ? (
                <div className="media-kit-interest-category">
                    <h4>{activeCategory.name}</h4>
                    <p className="media-kit-interest-select-label">SELECT FROM THE FOLLOWING</p>
                    <div className="media-kit-interest-pills-row">
                        <div className="media-kit-interest-pills">
                            {activeCategory.interests.map((interest) => (
                                <button
                                    key={interest.id}
                                    type="button"
                                    className={`media-kit-interest-pill${selectedInterestIds.includes(interest.id) ? ' selected' : ''}`}
                                    onClick={() => toggleInterest(interest.id)}
                                    data-testid={`media-kit-interest-${interest.id}`}
                                >
                                    {interest.name}
                                </button>
                            ))}
                        </div>
                        {interestCategories.length > 1 && (
                            <button
                                type="button"
                                className="media-kit-interest-category-next"
                                onClick={() =>
                                    setActiveCategoryIndex((index) => (index + 1) % interestCategories.length)
                                }
                                aria-label="Next interest category"
                                data-testid="media-kit-next-interest-category"
                            >
                                ▶
                            </button>
                        )}
                    </div>
                </div>
            ) : (
                <p className="media-kit-interests-empty">
                    Interest options are unavailable right now. You can type yours below.
                </p>
            )}

            <div className="media-kit-field-group media-kit-interests-custom">
                <label className="media-kit-field-label" htmlFor="media-kit-interests-custom-input">
                    Type your interests
                </label>
                <input
                    id="media-kit-interests-custom-input"
                    className="media-kit-input"
                    value={customDraft}
                    onChange={(e) => setCustomDraft(e.target.value)}
                    placeholder="e.g. Robotics, Photography"
                    data-testid="media-kit-custom-interests"
                />
            </div>

            {saveError && <span className="media-kit-error">{saveError}</span>}

            <div className="media-kit-interests-edit-actions">
                <button type="button" className="media-kit-btn-primary" onClick={save} disabled={saving}>
                    Save
                </button>
                <button type="button" className="media-kit-edit-cancel-btn" onClick={onCancel} disabled={saving}>
                    Cancel
                </button>
            </div>
        </div>
    );
}
